import { Dropdown, Icon, Menu, type MenuProps } from '@eleo-rh/core-ui'
import { toast } from '@eleo-rh/core-ui/contexts'
import axios from 'axios'
import Button from 'components/UI/CoreUIButton'
import SectionTitle from 'components/UI/SectionTitle'
import {
  API_URL_DOC_TEMPLATES,
  API_URL_DOC_TEMPLATES_ACCREDITATION_PASSPORT_DOWNLOAD as downloadURL,
  API_URL_USER_ACCREDITATION_PASSPORT_VALID_CERTIFICATE_WISHES as validCertificateWishesURL,
} from 'constants/api'
import { CONTEXT_ACCREDITATION_PASSPORT_CODE } from 'constants/docTemplate'
import { useI18n } from 'context/I18nProvider'
import { useBlob } from 'hooks/binary'
import useSWR from 'hooks/swr'
import * as React from 'react'
import { formatDate } from 'services/string'
import { urlAddParams } from 'services/url'

interface Value {
  value: number
  label: string
}

interface PassportCustomProps {
  userId: number
  onBack: () => void
}

interface DocTemplateOption {
  id: number | string
  formats: string[]
  translations?: Record<string, { name: string; locale: string }>
}

interface CertificateWishOption {
  id: number
  accreditationName: string
  endValidityDate?: string | null
}

export default function PassportCustom({ userId, onBack }: PassportCustomProps) {
  const { i18n, i18name } = useI18n()
  const { downloadBlob } = useBlob()

  const [posting, setPosting] = React.useState(false)
  const [template, setTemplate] = React.useState<Value | null>(null)
  const [certificateWishes, setCertificateWishes] = React.useState<Value[]>([])

  const { data: templates } = useSWR(
    urlAddParams(API_URL_DOC_TEMPLATES, { context: CONTEXT_ACCREDITATION_PASSPORT_CODE }),
  ) as { data: DocTemplateOption[] | undefined }
  const { data: validCertificateWishes } = useSWR(validCertificateWishesURL.replace('ID', String(userId))) as {
    data: CertificateWishOption[] | undefined
  }

  const templateOptions: Value[] = (templates || []).map(t => ({ value: Number(t.id), label: i18name(t) }))
  const certificateWishOptions: Value[] = (validCertificateWishes || []).map(cw => ({
    value: cw.id,
    label: cw.endValidityDate ? `${cw.accreditationName} (${formatDate(cw.endValidityDate)})` : cw.accreditationName,
  }))

  const selectedTemplate = (templates || []).find(t => Number(t.id) === template?.value)
  const formats: string[] = selectedTemplate?.formats || []

  const canGenerate = !!template && certificateWishes.length > 0

  const handleGenerate = async (format: string) => {
    const downloadUrl = urlAddParams(downloadURL, {
      format,
      user: userId,
      template: template?.value,
      certificateWishes: certificateWishes.map(cw => cw.value),
    })
    setPosting(true)
    try {
      const downloaded = await downloadBlob(downloadUrl, '_blank', undefined, {
        validateStatus: (status: number) => status >= 200 && status < 300,
      })
      if (downloaded) {
        toast.success({ title: i18n('notification.generic.success'), duration: 3 })
        onBack()
      }
    } catch (error) {
      toast.error({
        title: i18n('label.error'),
        description:
          (axios.isAxiosError<{ 'hydra:description'?: string }>(error) &&
            error.response?.data?.['hydra:description']) ||
          i18n('accreditation.passport.generation_failed'),
        duration: 3,
      })
    } finally {
      setPosting(false)
    }
  }

  const handleMenuClick: MenuProps['onClickItems'] = ({ key }) => {
    handleGenerate(key)
  }

  const generateItems: MenuProps['items'] = [
    ...(formats.includes('docx') ? [{ label: 'Word', key: 'docx', icon: <Icon name="file-word" /> }] : []),
    ...(formats.includes('pdf') ? [{ label: 'Pdf', key: 'pdf', icon: <Icon name="file-pdf" /> }] : []),
  ]

  return (
    <div className="flex flex-col gap-8">
      <Button variant="tertiary" leftIcon="arrow-left" onClick={onBack}>
        {i18n('label.back')}
      </Button>

      <SectionTitle>{i18n('accreditation.passport.custom.modal_title')}</SectionTitle>

      <div className="mt-4">
        <div className="mb-2">{i18n('accreditation.passport.custom.template_label')}</div>
        <Dropdown
          options={templateOptions}
          value={template}
          labelInValue
          showSearch={{ optionFilterProp: 'label' }}
          placeholder=" "
          onChange={option => setTemplate((option as Value | undefined) ?? null)}
          aria-required
        />

        <div className="mb-2 mt-4">{i18n('accreditation.passport.custom.certificate_wishes_label')}</div>
        <Dropdown
          options={certificateWishOptions}
          value={certificateWishes}
          labelInValue
          mode="multiple"
          showSearch={{ optionFilterProp: 'label' }}
          placeholder=" "
          onChange={options => setCertificateWishes((options as Value[]) || [])}
          aria-required
        />
      </div>

      <div className="flex justify-end mt-6">
        {formats.length > 1 ? (
          <Menu items={generateItems} onClickItems={handleMenuClick} disabled={!canGenerate}>
            <Button disabled={!canGenerate} loading={posting} rightIcon="chevron-down">
              {i18n('accreditation.passport.custom.generate')}
            </Button>
          </Menu>
        ) : (
          <Button disabled={!canGenerate} loading={posting} onClick={() => handleGenerate(formats[0])}>
            {i18n('accreditation.passport.custom.generate')}
          </Button>
        )}
      </div>
    </div>
  )
}
